import { Container, FederatedEvent, Graphics, IDestroyOptions, Point, Rectangle, Sprite, Text, TEXT_GRADIENT, TextStyle, Texture } from "pixi.js";
import { Event } from "../../../../../../../../shared/event-system/Event";
import { TokenType } from "../../../../../../../../web3/TokenType";
import { HBox } from "../../../../../../common/components/ui/HBox";
import { GameFont } from "../../../../../../common/GameFont";
import { TextWithUnderline } from "../../../../../../game/components/TextWithUnderline";

export class BaseTokenPanel extends Container {
    private static readonly WALLET_TRANSACTIONS_TEXT = "SEE IN-GAME WALLET TRANSACTIONS HERE";

    public readonly onPanelClick: Event<TokenType>;
    public readonly onSeeTransactionsClick: Event<TokenType>;

    private readonly background: Graphics;
    private readonly tokenIcon: Sprite;
    private readonly tokenTypeText: Text;
    private readonly tokenDescriptionText: Text;
    private readonly balanceBox: HBox;
    private readonly balanceIcon: Sprite;
    private readonly balanceText: Text;
    private readonly transactionsText: TextWithUnderline;

    public set balance(value: string) {
        this.balanceText.text = value;
    }

    public constructor(
        private readonly tokenType: TokenType,
        panelSize: Point,
        tokenTexture: Texture,
        tokenTextStyle: TextStyle,
        tokenDescription: string,
        balance: string
    ) {
        super();
        this.onPanelClick = new Event();
        this.onSeeTransactionsClick = new Event();

        this.background = new Graphics().beginFill(0xFFFFFF).drawRect(0, 0, panelSize.x, panelSize.y);
        this.background.alpha = 0;

        this.tokenIcon = new Sprite(tokenTexture);
        this.tokenIcon.width = this.tokenIcon.height = 120;
        this.tokenIcon.position.set(37, 0);

        this.tokenTypeText = new Text(TokenType[this.tokenType], tokenTextStyle);
        this.tokenDescriptionText = new Text(tokenDescription, new DescriptionTextStyle());
        this.tokenTypeText.position.set(this.tokenIcon.position.x + this.tokenIcon.width + 10, this.tokenIcon.position.y + this.tokenIcon.height - this.tokenTypeText.height - this.tokenDescriptionText.height - 6);
        this.tokenDescriptionText.position.set(this.tokenTypeText.position.x, this.tokenTypeText.position.y + this.tokenTypeText.height);

        this.balanceBox = new HBox(5);
        this.balanceText = new Text(balance, new BalanceTextStyle());
        this.balanceIcon = new Sprite(tokenTexture);
        this.balanceIcon.width = this.balanceIcon.height = 60;
        this.balanceBox.addChild(this.balanceText, this.balanceIcon);
        this.balanceText.position.y = (this.balanceIcon.height - this.balanceText.height) * 0.5 + 3;
        this.balanceBox.position.set(this.background.width - this.balanceBox.width - 40, this.tokenTypeText.position.y - 10);

        this.transactionsText = new TextWithUnderline(BaseTokenPanel.WALLET_TRANSACTIONS_TEXT, new WalletTransactionsTextStyle());
        this.transactionsText.position.set((this.background.width - this.transactionsText.width) * 0.5, this.background.height - this.transactionsText.height - 5);
        this.transactionsText.hitArea = new Rectangle(0, -this.transactionsText.height * 0.5, this.transactionsText.width, this.transactionsText.height * 2)
        this.transactionsText.interactive = true;

        this.addChild(
            this.background,
            this.tokenIcon,
            this.tokenTypeText,
            this.tokenDescriptionText,
            this.balanceBox,
            this.transactionsText
        );
        this.interactive = true;

        this.on("pointerup", this.onPointerUp, this);
        this.transactionsText.on("pointerup", this.onSeeTransactionsTextClick, this);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.off("pointerup", this.onPointerUp, this);
        this.transactionsText.off("pointerup", this.onSeeTransactionsTextClick, this);
        super.destroy(options);
    }

    private onPointerUp(): void {
        this.onPanelClick.emit(this.tokenType);
    }

    private onSeeTransactionsTextClick(event: FederatedEvent): void {
        event.stopPropagation();
        this.onSeeTransactionsClick.emit(this.tokenType);
    }

    public layout(): void {
        this.balanceBox?.alignmentStrategy();
        this.balanceText.position.y = (this.balanceIcon.height - this.balanceText.height) * 0.5;
        this.balanceBox.position.x = this.background.width - this.balanceBox.width - 40;
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 30,
            fontWeight: "400",
            fill: "#FFFFFF"
        });
    }
}

class BalanceTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48,
            fontWeight: "400",
            fill: "#FFFFFF"
        });
    }
}

class WalletTransactionsTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 32,
            fill: "#2596FF",
            fontWeight: "400",
            lineHeight: 30
        });
    }
}
